/**
 * Servizio di logging per l'app Pipeline
 * Registra tutte le attivit� e gli errori in localStorage
 */

export type LogLevel = 'info' | 'warn' | 'error' | 'debug' | 'success';

export interface LogEntry {
  id: string;
  timestamp: string;
  level: LogLevel;
  category: string;
  message: string;
  details?: string;
  stack?: string;
}

// Chiave per localStorage
const LOG_KEY = 'pipeline_activity_log';
const MAX_LOG_ENTRIES = 500; // Limite massimo di log da conservare

// Funzione per generare ID univoco
const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

/**
 * Servizio di logging centralizzato
 */
export const logService = {
  /**
   * Ottiene tutti i log dal localStorage
   */
  getAll: (): LogEntry[] => {
    const stored = localStorage.getItem(LOG_KEY);
    if (stored) {
      try {
        return JSON.parse(stored) as LogEntry[];
      } catch {
        return [];
      }
    }
    return [];
  },

  /**
   * Ottiene i log filtrati per livello
   */
  getByLevel: (level: LogLevel): LogEntry[] => {
    return logService.getAll().filter(log => log.level === level);
  },

  /**
   * Ottiene i log filtrati per categoria
   */
  getByCategory: (category: string): LogEntry[] => {
    return logService.getAll().filter(log => log.category === category);
  },

  /**
   * Aggiunge un nuovo log
   */
  add: (level: LogLevel, category: string, message: string, details?: string, stack?: string): LogEntry => {
    const entry: LogEntry = {
      id: generateId(),
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      details,
      stack,
    };

    const logs = logService.getAll();
    logs.unshift(entry); // Aggiungi in cima

    // Mantieni solo gli ultimi MAX_LOG_ENTRIES
    if (logs.length > MAX_LOG_ENTRIES) {
      logs.splice(MAX_LOG_ENTRIES);
    }

    localStorage.setItem(LOG_KEY, JSON.stringify(logs));

    // Log anche in console per debug
    const consoleMethod = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
    consoleMethod(`[${level.toUpperCase()}] [${category}] ${message}`, details || '');

    return entry;
  },

  /**
   * Log informativo
   */
  info: (category: string, message: string, details?: string): LogEntry => {
    return logService.add('info', category, message, details);
  },

  /**
   * Log di warning
   */
  warn: (category: string, message: string, details?: string): LogEntry => {
    return logService.add('warn', category, message, details);
  },

  /**
   * Log di errore
   */
  error: (category: string, message: string, error?: Error | unknown): LogEntry => {
    let details: string | undefined;
    let stack: string | undefined;

    if (error instanceof Error) {
      details = error.message;
      stack = error.stack;
    } else if (error) {
      details = String(error);
    }

    return logService.add('error', category, message, details, stack);
  },

  /**
   * Log di debug
   */
  debug: (category: string, message: string, details?: string): LogEntry => {
    return logService.add('debug', category, message, details);
  },

  /**
   * Log di successo
   */
  success: (category: string, message: string, details?: string): LogEntry => {
    return logService.add('success', category, message, details);
  },

  /**
   * Svuota tutti i log
   */
  clear: (): void => {
    localStorage.removeItem(LOG_KEY);
  },

  /**
   * Elimina un singolo log
   */
  delete: (id: string): boolean => {
    const logs = logService.getAll();
    const index = logs.findIndex(log => log.id === id);
    if (index === -1) return false;

    logs.splice(index, 1);
    localStorage.setItem(LOG_KEY, JSON.stringify(logs));
    return true;
  },

  /**
   * Ottiene il conteggio degli errori non letti (ultimi 24h)
   */
  getRecentErrorCount: (): number => {
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    return logService.getAll().filter(
      log => log.level === 'error' && log.timestamp > oneDayAgo
    ).length;
  },

  /**
   * Esporta i log come stringa JSON
   */
  export: (): string => {
    return JSON.stringify(logService.getAll(), null, 2);
  },

  /**
   * Esporta i log come file scaricabile
   */
  downloadLogs: (): void => {
    const logs = logService.export();
    const blob = new Blob([logs], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pipeline-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};

// Categorie predefinite per i log
export const LogCategory = {
  AUTH: 'Autenticazione',
  RAPPORTI: 'Rapporti',
  SYNC: 'Sincronizzazione',
  API: 'API',
  COMMESSE: 'Commesse',
  ALLEGATI: 'Allegati',
  APP: 'Applicazione',
  NETWORK: 'Rete',
} as const;

export default logService;
